import { useState } from 'react';

import { CheckCircleFilled, CheckCircleOutlined } from '@ant-design/icons';
import {
  BANK_CODES_FOR_ALL,
  BankCodesForAll,
  ShortedBankName,
  getOrganizationLogoForAll,
} from '@revenuemarket/bank';
import { Flex } from '@revenuemarket/cds';
import { colorTokens, thematicColors } from '@revenuemarket/design';
import { AntColumnType } from '@revenuemarket/ui/antd/Table';
import { Button, Modal, ModalProps, Table, Typography } from 'antd';
import Image from 'next/image';

import { WiringResult, WiringResultItem } from '~apis/wiring';
import { useGetRecentWiringList } from '~hooks/queries/wiring/query';
import { useCompanyId } from '~hooks/useCompanyId';
import { css } from '~libs/emotion';

import { wiringTypeRender } from './renderComponent';

interface Props extends ModalProps {
  onClose?: () => void;
  onComplete?: (data: WiringResultItem[]) => void;
}

const COLUMNS = ({
  checkedItems,
  onCheck,
}: {
  checkedItems: number[];
  onCheck: (id: number) => void;
}): AntColumnType<WiringResult> => {
  return [
    {
      title: '이체명',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: '입금 예금주',
      dataIndex: 'receiverName',
      key: 'receiverName',
      render: (value, record) => {
        return record.recordList?.length > 1
          ? record.recordList[0]?.receiverName + ` 외 ${record.recordList?.length - 1}건`
          : record.recordList[0]?.receiverName;
      },
    },
    {
      title: '합계 금액',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (_, record) => {
        return record.recordList.reduce((acc, cur) => acc + cur.amount, 0).toLocaleString();
      },
    },
    {
      title: '거래 구분',
      dataIndex: 'wiringType',
      key: 'wiringType',
      render: (value) => {
        return wiringTypeRender(value);
      },
    },
    {
      title: '내 통장 적요',
      dataIndex: 'senderDescription',
      key: 'senderDescription',
      render: (value, record) => {
        const repSenderDescription = record.recordList.find((record) => {
          return Boolean(record.senderDescription);
        })?.senderDescription;
        return record.recordList?.length > 1
          ? repSenderDescription
            ? repSenderDescription + `외 ${record.recordList.length - 1}건`
            : ''
          : repSenderDescription;
      },
    },
    {
      title: '받는 분 적요',
      dataIndex: 'receiverDescription',
      key: 'receiverDescription',
      render: (value, record) => {
        const repReceiverDescription = record.recordList.find((record) => {
          return Boolean(record.receiverDescription);
        })?.receiverDescription;
        return record.recordList?.length > 1
          ? repReceiverDescription
            ? repReceiverDescription + `외 ${record.recordList.length - 1}건`
            : ''
          : repReceiverDescription;
      },
    },
    {
      title: '메모',
      dataIndex: 'memo',
      key: 'memo',
      render: (value, record) => {
        const repMemo = record.recordList.find((record) => {
          return Boolean(record.memo);
        })?.memo;
        return record.recordList?.length > 1
          ? repMemo
            ? repMemo + `외 ${record.recordList.length - 1}건`
            : ''
          : repMemo;
      },
    },
    {
      title: '출금 계좌',
      dataIndex: 'wiringBank',
      key: 'wiringBank',
      render: (value, record) => {
        const wiringBank = (record.wiringBank === 'HANA' ? '081' : '004') as BankCodesForAll;
        return (
          <Flex gap={10} align="center">
            <Image
              width={16}
              height={16}
              src={
                getOrganizationLogoForAll(wiringBank)?.x2 ||
                'https://static.clobe.ai/image/etc-bank.png'
              }
              alt={BANK_CODES_FOR_ALL[wiringBank]}
            />
            <Typography>{`${
              record.accountAlias ||
              `${
                ShortedBankName[record.wiringBank === 'HANA' ? '081' : '004']
              } ${record.accountNumber.replaceAll('-', '').slice(-4)}`
            }`}</Typography>
          </Flex>
        );
      },
    },
    {
      title: '',
      key: 'check',
      render: (_, record) => {
        const checked = checkedItems.includes(record?.id ?? 0);
        return (
          <Button type="text">
            {checked ? (
              <CheckCircleFilled
                style={{
                  fontSize: 20,
                  color: thematicColors.primary.medium,
                }}
              />
            ) : (
              <CheckCircleOutlined
                style={{
                  color: colorTokens.iconDefault,
                  fontSize: 20,
                }}
              />
            )}
          </Button>
        );
      },
    },
  ];
};

const RecentWiringHistoryModal: React.FC<Props> = ({ ...props }) => {
  const [checkedItems, setCheckedItems] = useState<number[]>([]);
  const [keyword, setKeyword] = useState<string>('');
  const { companyId } = useCompanyId();
  const { data } = useGetRecentWiringList({ companyId, keyword });
  const recentWiringList =
    data?.Data?.data?.items.map((item, index) => {
      return {
        ...item,
        key: item.id,
      };
    }) || [];
  const onCheck = (id: number) => {
    setCheckedItems((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }
      return [...prev, id];
    });
  };

  return (
    <Modal
      {...props}
      title="최근 이체 불러오기"
      width="80vw"
      cancelText="취소"
      okText={checkedItems.length ? `총 ${checkedItems.length}개 선택 입력` : '다음'}
      onOk={() => {
        props.onComplete?.(
          recentWiringList
            .filter((item) => checkedItems.includes(item?.id ?? 0))
            .map((item) => {
              return item.recordList;
            })
            .flat(),
        );
      }}
      okButtonProps={{
        disabled: !checkedItems.length,
      }}
    >
      <Flex
        direction="column"
        gap={24}
        style={{
          marginTop: 16,
        }}
      >
        <Table
          columns={COLUMNS({ checkedItems, onCheck })}
          dataSource={recentWiringList}
          className={css`
            &&& .ant-table-thead th:first-child {
              border-radius: 0px;
            }
            &&& .ant-table-thead th:last-child {
              border-radius: 0px;
            }
          `}
          onRow={(data) => {
            return {
              style: {
                backgroundColor: checkedItems.includes(data?.id ?? 0) ? '#F0FFFC' : '',
              },
              onClick: () => {
                onCheck(data?.id ?? 0);
              },
            };
          }}
        />
      </Flex>
    </Modal>
  );
};

export default RecentWiringHistoryModal;
